import React, { useEffect, useMemo, useState } from 'react';
import { API_BASE_URL, fetchJson } from './api';
import {
    Box,
    Button,
    Chip,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';
import SyncAltRoundedIcon from '@mui/icons-material/SyncAltRounded';
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { alpha } from '@mui/material/styles';
import { AnalyticsInlineState, AnalyticsMetricCard, AnalyticsPageHero, AnalyticsPanelSection, glassSubCardSx } from './analyticsSurface';

const ACTIVITY_FILTERS_STORAGE_KEY = 'monsurv_activity_filters_v1';

function loadSavedFilters() {
    try {
        const raw = localStorage.getItem(ACTIVITY_FILTERS_STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : null;
        if (!parsed || typeof parsed !== 'object') {
            return {
                referenceInput: '',
                filterText: '',
                filterAction: 'all',
                filterResult: 'all'
            };
        }

        return {
            referenceInput: safeText(parsed.referenceInput || ''),
            filterText: safeText(parsed.filterText || ''),
            filterAction: safeText(parsed.filterAction || 'all').toLowerCase() || 'all',
            filterResult: safeText(parsed.filterResult || 'all').toLowerCase() || 'all'
        };
    } catch {
        return {
            referenceInput: '',
            filterText: '',
            filterAction: 'all',
            filterResult: 'all'
        };
    }
}

function getAuthTokenFromStorage() {
    try {
        return String(localStorage.getItem('monsurv_auth_token') || '').trim();
    } catch {
        return '';
    }
}

function defaultAuthHeaders(extra) {
    const h = { ...(extra || {}) };
    const token = getAuthTokenFromStorage();
    if (token) h.Authorization = `Bearer ${token}`;
    return h;
}

function formatWhen(value) {
    try {
        const d = value ? new Date(value) : null;
        if (!d || !Number.isFinite(d.getTime())) return '';
        const pad2 = (n) => String(n).padStart(2, '0');
        const dd = pad2(d.getDate());
        const mm = pad2(d.getMonth() + 1);
        const yy = String(d.getFullYear()).slice(-2);
        const hh = pad2(d.getHours());
        const min = pad2(d.getMinutes());
        return `${dd}/${mm}/${yy} ${hh}:${min}`;
    } catch {
        return '';
    }
}

function safeText(value, fallback = '') {
    const s = value == null ? '' : String(value);
    const t = s.trim();
    return t || fallback;
}

function actionLabel(action) {
    const a = safeText(action).toUpperCase();
    if (!a) return 'EVENT';
    return a.replace(/_/g, ' ');
}

function summarizeMetadata(meta, projectReference) {
    const m = meta && typeof meta === 'object' ? meta : {};
    const ref = safeText(m.project_reference || m.projectRef || projectReference || '');
    const parts = [];
    if (ref) parts.push(`Project ${ref}`);

    const reportLabel = safeText(m.report_label || '');
    if (reportLabel) parts.push(reportLabel);
    else {
        const reportType = safeText(m.report_type || '');
        const revision = m.revision != null && Number.isFinite(Number(m.revision)) ? Number(m.revision) : null;

        const reportInstance = safeText(m.report_instance || '');
        const interimFromInstance = (() => {
            const s = reportInstance.toLowerCase();
            if (!s.startsWith('interim:')) return null;
            const raw = s.slice('interim:'.length);
            const n = Number(raw);
            return Number.isFinite(n) && n > 0 ? n : null;
        })();

        const interimNumber = m.interim_number != null && Number.isFinite(Number(m.interim_number))
            ? Number(m.interim_number)
            : interimFromInstance;

        if (reportType && revision != null) {
            if (/^interim\s*report$/i.test(reportType) && interimNumber != null) {
                parts.push(`Interim Report ${interimNumber} Version ${revision}`);
            } else {
                parts.push(`${reportType} Version ${revision}`);
            }
        }
    }

    if (m.condition_id) parts.push(`Condition #${m.condition_id}`);
    if (m.status) parts.push(`Status: ${safeText(m.status)}`);

    if (Number.isFinite(Number(m.inserted_policies))) parts.push(`Policies: ${Number(m.inserted_policies)}`);
    if (Number.isFinite(Number(m.inserted_consultants))) parts.push(`Consultants: ${Number(m.inserted_consultants)}`);

    return parts.join(' • ');
}

export default function ActivityFeed({
    reference: referenceProp = '',
    mode = 'dashboard',
    authHeaders,
    defaultLimit = 80,
    siteWideOnly = false,
}) {
    const savedFilters = useMemo(() => loadSavedFilters(), []);
    const [referenceInput, setReferenceInput] = useState(() => safeText(referenceProp || savedFilters.referenceInput));

    const [filterText, setFilterText] = useState(() => savedFilters.filterText);
    const [filterAction, setFilterAction] = useState(() => savedFilters.filterAction);
    const [filterResult, setFilterResult] = useState(() => savedFilters.filterResult);

    const headersFn = useMemo(() => {
        return typeof authHeaders === 'function' ? authHeaders : defaultAuthHeaders;
    }, [authHeaders]);

    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const actionOptions = useMemo(() => {
        const set = new Map();
        for (const it of (Array.isArray(items) ? items : [])) {
            const raw = safeText(it?.action || '').toUpperCase();
            if (!raw) continue;
            set.set(raw, actionLabel(raw));
        }
        return Array.from(set.entries())
            .map(([value, label]) => ({ value, label }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [items]);

    const filteredItems = useMemo(() => {
        const list = Array.isArray(items) ? items : [];
        const q = safeText(filterText).toLowerCase();
        const action = safeText(filterAction).toLowerCase();
        const result = safeText(filterResult).toLowerCase();

        if (!q && (action === 'all' || !action) && (result === 'all' || !result)) return list;

        return list.filter((it) => {
            const success = it?.success !== false;

            if (action && action !== 'all') {
                const a = safeText(it?.action || '').toLowerCase();
                if (a !== action) return false;
            }

            if (result === 'success' && !success) return false;
            if (result === 'failed' && success) return false;

            if (q) {
                const actor = safeText(it?.actor_username || '', 'System');
                const role = safeText(it?.actor_role || '');
                const label = actionLabel(it?.action);
                const metaSummary = summarizeMetadata(it?.metadata, it?.project_reference);
                const ref = safeText(it?.project_reference || '');
                const haystack = [label, actor, role, ref, metaSummary].join(' ').toLowerCase();
                if (!haystack.includes(q)) return false;
            }

            return true;
        });
    }, [filterAction, filterResult, filterText, items]);

    const metrics = useMemo(() => {
        const list = Array.isArray(items) ? items : [];
        const filtered = Array.isArray(filteredItems) ? filteredItems : [];
        const failedCount = list.filter((it) => it?.success === false).length;
        const successCount = list.length - failedCount;
        const uniqueActors = new Set(
            list.map((it) => safeText(it?.actor_username || '', 'System')).filter(Boolean)
        ).size;
        return {
            total: list.length,
            filtered: filtered.length,
            successCount,
            failedCount,
            uniqueActors,
        };
    }, [filteredItems, items]);

    const endpoint = useMemo(() => {
        const limit = Math.max(1, Math.min(Number(defaultLimit || 80), 250));

        if (mode === 'portal') {
            const ref = safeText(referenceProp);
            if (!ref) return '';
            return `${API_BASE_URL}/project/${encodeURIComponent(ref)}/activity?limit=${encodeURIComponent(limit)}`;
        }

        // Dashboard/global view
        const ref = siteWideOnly ? '' : safeText(referenceInput);
        const qs = new URLSearchParams();
        qs.set('limit', String(limit));
        if (ref) qs.set('reference', ref);
        return `${API_BASE_URL}/api/activity?${qs.toString()}`;
    }, [defaultLimit, mode, referenceInput, referenceProp, siteWideOnly]);

    async function load({ silent } = {}) {
        if (!endpoint) return;
        setLoading(true);
        if (!silent) setError('');
        try {
            const json = await fetchJson(endpoint, { method: 'GET', headers: headersFn() });
            // Support both modern `{ ok: true, items: [...] }` and legacy `[...]` responses.
            const next = Array.isArray(json)
                ? json
                : (Array.isArray(json?.items)
                    ? json.items
                    : (Array.isArray(json?.activity)
                        ? json.activity
                        : []));
            setItems(next);
            setError('');
        } catch (e) {
            if (!silent) console.warn('ActivityFeed load failed:', e);
            setItems([]);
            setError(e?.message || 'Failed to load activity');
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        load({ silent: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [endpoint]);

    function resetFilters() {
        if (!siteWideOnly) setReferenceInput('');
        setFilterText('');
        setFilterAction('all');
        setFilterResult('all');
    }

    useEffect(() => {
        try {
            localStorage.setItem(ACTIVITY_FILTERS_STORAGE_KEY, JSON.stringify({
                referenceInput,
                filterText,
                filterAction,
                filterResult
            }));
        } catch {
            // ignore
        }
    }, [referenceInput, filterText, filterAction, filterResult]);

    return (
        <Box sx={{ display: 'grid', gap: 2.25 }}>
            <AnalyticsPageHero
                eyebrow="Operational Timeline"
                title="Activity and audit history"
                chips={[
                    mode === 'portal' ? 'Project-scoped feed' : 'Dashboard feed',
                    siteWideOnly ? 'Site-wide only' : 'Reference-aware'
                ]}
                actions={
                    <Button variant="contained" onClick={() => load({ silent: false })} disabled={loading || !endpoint}>
                        Refresh feed
                    </Button>
                }
            />

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(4, minmax(0, 1fr))' },
                    gap: 2,
                }}
            >
                <AnalyticsMetricCard
                    icon={<HistoryRoundedIcon fontSize="small" />}
                    label="Events Loaded"
                    value={metrics.total}
                    caption="Full event volume returned by the current endpoint before local filtering."
                    accent="linear-gradient(135deg, #2563eb, #0f172a)"
                />
                <AnalyticsMetricCard
                    icon={<InsightsRoundedIcon fontSize="small" />}
                    label="Visible Now"
                    value={metrics.filtered}
                    caption="Events matching the current search, action, and result filters."
                    accent="linear-gradient(135deg, #0f766e, #14b8a6)"
                />
                <AnalyticsMetricCard
                    icon={<TaskAltRoundedIcon fontSize="small" />}
                    label="Successful"
                    value={metrics.successCount}
                    caption="Completed actions that did not register a failure state."
                    accent="linear-gradient(135deg, #15803d, #22c55e)"
                />
                <AnalyticsMetricCard
                    icon={<WarningAmberRoundedIcon fontSize="small" />}
                    label="Failures"
                    value={metrics.failedCount}
                    caption="Failed actions surfaced in the same audit stream for quick follow-up."
                    accent="linear-gradient(135deg, #b45309, #ef4444)"
                />
            </Box>

            <AnalyticsPanelSection
                eyebrow="Feed Controls"
                title="Filters and scope"
                right={
                    !loading && !error && metrics.total > 0 ? (
                        <Chip
                            label={`Showing ${metrics.filtered} of ${metrics.total}`}
                            sx={{ fontWeight: 800, backgroundColor: alpha('#ffffff', 0.62) }}
                        />
                    ) : null
                }
            >
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', xl: '180px 220px 170px 150px auto' },
                        gap: 1.5,
                        alignItems: 'start'
                    }}
                >
                    {mode === 'dashboard' && !siteWideOnly ? (
                        <TextField
                            size="small"
                            label="Project ref"
                            value={referenceInput}
                            onChange={(e) => setReferenceInput(e.target.value)}
                            placeholder="e.g. M340"
                        />
                    ) : null}

                    <TextField
                        size="small"
                        label="Filter"
                        value={filterText}
                        onChange={(e) => setFilterText(e.target.value)}
                        placeholder="Search activity…"
                    />

                    <FormControl size="small">
                        <InputLabel id="activity-filter-action">Action</InputLabel>
                        <Select
                            labelId="activity-filter-action"
                            label="Action"
                            value={filterAction}
                            onChange={(e) => setFilterAction(e.target.value)}
                        >
                            <MenuItem value="all">All</MenuItem>
                            {actionOptions.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value.toLowerCase()}>
                                    {opt.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl size="small">
                        <InputLabel id="activity-filter-result">Result</InputLabel>
                        <Select
                            labelId="activity-filter-result"
                            label="Result"
                            value={filterResult}
                            onChange={(e) => setFilterResult(e.target.value)}
                        >
                            <MenuItem value="all">All</MenuItem>
                            <MenuItem value="success">Success</MenuItem>
                            <MenuItem value="failed">Failed</MenuItem>
                        </Select>
                    </FormControl>

                    <Paper sx={{ ...glassSubCardSx, minHeight: 56, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5 }}>
                        <Stack spacing={0.25}>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                                Active Contributors
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 800 }}>
                                {metrics.uniqueActors} contributors in this window
                            </Typography>
                        </Stack>
                        <Box sx={{ width: 38, height: 38, borderRadius: '50%', display: 'grid', placeItems: 'center', backgroundColor: alpha('#e2e8f0', 0.8), color: 'text.primary' }}>
                            <SyncAltRoundedIcon fontSize="small" />
                        </Box>
                    </Paper>

                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Button variant="outlined" size="small" onClick={resetFilters}>
                            Reset filters
                        </Button>
                    </Box>
                </Box>
            </AnalyticsPanelSection>

            <AnalyticsPanelSection
                eyebrow="Audit Timeline"
                title="Event stream"
            >
                {loading ? (
                    <AnalyticsInlineState kind="loading" message="Loading activity..." compact />
                ) : null}

                {!loading && error ? (
                    <AnalyticsInlineState
                        kind="error"
                        title="Unable to load activity"
                        message={error}
                        actionLabel="Retry"
                        onAction={() => load({ silent: false })}
                    />
                ) : null}

                {!loading && !error && (!items || !items.length) ? (
                    <AnalyticsInlineState
                        kind="empty"
                        title="No activity found"
                        message="No events were returned for the selected scope yet."
                    />
                ) : null}

                {!loading && !error && items && items.length > 0 && filteredItems.length === 0 ? (
                    <AnalyticsInlineState
                        kind="empty"
                        title="No matching activity"
                        message="Try broadening your search or resetting one of the filters."
                    />
                ) : null}

                {!loading && !error && filteredItems && filteredItems.length > 0 ? (
                    <Stack spacing={1.5}>
                        {filteredItems.map((it) => {
                            const when = formatWhen(it.created_at);
                            const actor = safeText(it.actor_username || '', 'System');
                            const role = safeText(it.actor_role || '');
                            const label = actionLabel(it.action);
                            const metaSummary = summarizeMetadata(it.metadata, it.project_reference);
                            const success = it.success !== false;

                            return (
                                <Paper
                                    key={String(it.id)}
                                    sx={{
                                        p: { xs: 1.6, sm: 1.8 },
                                        borderRadius: 1.75,
                                        border: `1px solid ${alpha(success ? '#bfdbfe' : '#fecaca', 0.7)}`,
                                        background: success
                                            ? `linear-gradient(180deg, ${alpha('#ffffff', 0.9)} 0%, ${alpha('#f8fbff', 0.72)} 100%)`
                                            : `linear-gradient(180deg, ${alpha('#fff7f7', 0.96)} 0%, ${alpha('#fff1f2', 0.82)} 100%)`,
                                        boxShadow: '0 16px 36px rgba(15, 23, 42, 0.08)'
                                    }}
                                >
                                    <Stack spacing={1}>
                                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
                                            <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, flexWrap: 'wrap' }}>
                                                <Chip
                                                    size="small"
                                                    label={label}
                                                    sx={{
                                                        fontWeight: 900,
                                                        color: success ? '#1d4ed8' : '#b91c1c',
                                                        backgroundColor: success ? alpha('#dbeafe', 0.9) : alpha('#fee2e2', 0.92)
                                                    }}
                                                />
                                                <Typography variant="body2" sx={{ fontWeight: 900 }}>
                                                    {actor}
                                                </Typography>
                                                {role ? (
                                                    <Chip
                                                        size="small"
                                                        variant="outlined"
                                                        label={role}
                                                        sx={{ fontWeight: 800, textTransform: 'capitalize', backgroundColor: alpha('#ffffff', 0.48) }}
                                                    />
                                                ) : null}
                                            </Stack>

                                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 800 }}>
                                                {when}
                                            </Typography>
                                        </Stack>

                                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) auto' }, gap: 1.25, alignItems: 'start' }}>
                                            <Typography variant="body2" sx={{ color: 'text.primary' }}>
                                                {metaSummary || 'No extra metadata supplied for this event.'}
                                            </Typography>
                                            <Paper sx={{ ...glassSubCardSx, minWidth: { lg: 180 } }}>
                                                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                                    Outcome
                                                </Typography>
                                                <Typography variant="body2" sx={{ mt: 0.35, fontWeight: 800, color: success ? 'success.dark' : 'error.dark' }}>
                                                    {success ? 'Success' : 'Failure'}
                                                </Typography>
                                            </Paper>
                                        </Box>
                                    </Stack>
                                </Paper>
                            );
                        })}
                    </Stack>
                ) : null}
            </AnalyticsPanelSection>
        </Box>
    );
}
